import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Plus } from '@gravity-ui/icons';
import { Button, Icon, Tooltip } from '@gravity-ui/uikit';

import { useAddSectionsMutation, useDeleteSectionMutation, useGetSectionsQuery } from 'store/api';

import { ADD_DESCRIPTION, CANCEL_VTN, SAVE } from '../constants';

import { SectionForm } from './utils/section-form';
import { AdminTableLoader } from './admin-table-loader';

import styles from '../styles.module.scss';
import { ProfileIcon } from 'features/ui-kit/profile-icon';

interface Subsection {
	id?: number;
	sectionName: string;
	code: string;
	parentCode: string;
}

interface Section {
	id?: number;
	sectionName: string;
	code: string;
	subsections: Subsection[];
}

interface FormValues {
	sections: Section[];
}

export const AdminTableSections = ({ isMobile }: any) => {
	const { data: sectionsData, error, isLoading, refetch } = useGetSectionsQuery();

	const [addSection] = useAddSectionsMutation();

	const [deleteSection, { isLoading: isDeleting }] = useDeleteSectionMutation();

	const methods = useForm<FormValues>({
		defaultValues: {
			sections:
				sectionsData?.map((section) => ({
					id: section.id,
					sectionName: section.sectionName,
					code: section.code,
					subsections: section.subsections || [],
				})) || [],
		},
		mode: 'onChange',
	});

	const groupedSections = methods.watch('sections') || [];

	useEffect(() => {
		if (sectionsData) {
			methods.reset({
				sections: sectionsData.map((section) => ({
					id: section.id,
					sectionName: section.sectionName,
					code: section.code,
					subsections: section.subsections || [],
				})),
			});
		}
	}, [sectionsData, methods]);

	const addNewSection = () => {
		methods.setValue('sections', [
			...groupedSections,
			{
				id: undefined,
				sectionName: '',
				code: '',
				subsections: [
					{ id: undefined, sectionName: '', code: '', parentCode: '' },
					{ id: undefined, sectionName: '', code: '', parentCode: '' },
				],
			},
		]);
	};

	const removeSection = (sectionId: number) => {
		const sections = methods.getValues('sections');
		const index = sections.findIndex((section) => section.id === sectionId);

		if (index === -1) {
			console.error('Не найден раздел с таким id');
			return;
		}

		const sectionToDelete = sections[index];
		if (sectionToDelete.id) {
			deleteSection(sectionToDelete.id)
				.unwrap()
				.then(() => {
					const updatedSections = [...sections];
					updatedSections.splice(index, 1);
					methods.setValue('sections', updatedSections);
					refetch();
					toast.success('Раздел успешно удален');
				})
				.catch((error) => {
					toast.error(`Ошибка удаления раздела: ${error.data?.message}`);
					console.error('Ошибка удаления раздела:', error);
				});
		} else {
			const updatedSections = [...sections];
			updatedSections.splice(index, 1);
			methods.setValue('sections', updatedSections);
		}
	};

	const onSubmit = (data: { sections: Section[] }) => {
		const renameEntries: any[] = [];
		const newEntries: any[] = [];

		data.sections.forEach((section: Section) => {
			const originalSection = sectionsData?.find((orig: Section) => orig.id === section.id);

			if (section.id && originalSection) {
				if (originalSection.sectionName !== section.sectionName) {
					renameEntries.push({
						id: section.id,
						sectionName: section.sectionName,
					});
				}

				section.subsections.forEach((sub: Subsection) => {
					if (!sub.id) {
						newEntries.push({
							sectionName: sub.sectionName,
							parentCode: section.sectionName,
						});
					} else {
						const originalSub = originalSection?.subsections.find(
							(origSub: Subsection) => origSub.id === sub.id,
						) as Subsection | undefined;

						if (originalSub && originalSub.sectionName !== sub.sectionName) {
							renameEntries.push({
								id: sub.id,
								sectionName: sub.sectionName,
								parentCode: section.sectionName,
							});
						}
					}
				});
			} else {
				newEntries.push({
					sectionName: section.sectionName,
				});

				section.subsections.forEach((sub: any) => {
					newEntries.push({
						sectionName: sub.sectionName,
						parentCode: section.sectionName,
					});
				});
			}
		});

		if (renameEntries.length > 0) {
			addSection(renameEntries)
				.unwrap()
				.then(() => {
					toast.success('Изменения успешно сохраненыы');
					refetch();
					console.log('RenameEntries:', renameEntries);
				})
				.catch((error: any) => {
					console.error('Ошибка при переименовании:', error);
					toast.error('Не удалось сохранить изменения');
				});
		}

		if (newEntries.length > 0) {
			addSection(newEntries)
				.unwrap()
				.then(() => {
					toast.success('Изменения успешно сохранены');
					refetch();
					console.log('NewEntries:', newEntries);
				})
				.catch((error: any) => {
					console.error('Ошибка добавления новых данных:', error);
					toast.error('Не удалось добавить раздел(ы)');
				});
		}

		if (!renameEntries.length && !newEntries.length) {
			console.log('Нет изменений для отправки.');
		}
	};

	return (
		<div className={styles.accounts}>
			{isMobile ? (
				<div className={styles.accounts__title_container}>
					<div className={styles.accounts__title_container__icon}>
						<ProfileIcon />
						<h2 className={styles.accounts__title}>Разделы и подразделы</h2>
					</div>
				</div>
			) : (
				<h2 className={styles.accounts__title}>Разделы и подразделы</h2>
			)}

			<div className={styles.accounts__table}>
				{isLoading && <AdminTableLoader />}

				{!isLoading && (
					<div className={styles.accounts__table__main}>
						<FormProvider {...methods}>
							<form onSubmit={methods.handleSubmit(onSubmit)}>
								<div className="admin-sections-forms">
									{groupedSections.map((group, index) => (
										<SectionForm
											key={index}
											id={group.id}
											sectionName={group.sectionName}
											subsections={group.subsections}
											register={methods.register}
											control={methods.control}
											index={index}
											removeSection={removeSection}
											code={group.code}
											deleteSection={deleteSection}
											refetch={refetch}
											isMobile={isMobile}
										/>
									))}
									<Button
										size="xl"
										className={styles.accounts__table__main__btn_add}
										view="outlined"
										onClick={addNewSection}
									>
										<Icon data={Plus} size={18} />
										{ADD_DESCRIPTION}
									</Button>
								</div>

								<div className={styles.accounts__table__main__btn_group}>
									<Button size="xl">{CANCEL_VTN}</Button>

									<Button
										className={styles.accounts__table__main__btn_group__save}
										size="xl"
										type="submit"
										// disabled={!isValid}
										// view={isValid ? 'action' : 'raised'}
									>
										{SAVE}
									</Button>
								</div>
							</form>
						</FormProvider>
					</div>
				)}
			</div>
		</div>
	);
};
